import { useEffect, useRef } from 'react';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';

export interface UnsavedGuardCopy {
  title: string;
  body: string;
  /** Label for the button that abandons the work. */
  discard: string;
  /** Label for the button that stays put. */
  keep: string;
}

/**
 * Stops a navigation, and a tab close, while there is unsaved work.
 *
 * `flows-builder.md` §4 item 1: `grep -rn "useBlocker|beforeunload"` returned
 * NOTHING across either console, so every exit lost work silently: the page's
 * own "Back to flow" button, both breadcrumb links, the app shell's sidebar,
 * the browser Back button and a tab close. The flow builder even computed
 * `dirty` and rendered an "Unsaved changes" badge for it, then did nothing
 * with it.
 *
 * **This deliberately does not use `useBlocker`.** That hook only works inside
 * a data router (`createBrowserRouter`), and both consoles mount
 * `<BrowserRouter>`; calling it threw "useBlocker must be used within a data
 * router" and took the whole flow builder down with it. Migrating the router is
 * the supported fix and is a bigger change than this hook deserves, so the
 * three exits are covered directly:
 *
 * - **A link click.** Caught in the capture phase, before the router sees it.
 *   This is the sidebar, the breadcrumbs and every in-page link, which is
 *   nearly all navigation.
 * - **Back and Forward.** A sentinel history entry is pushed while dirty, so
 *   the first Back lands on it rather than leaving; the guard then asks, and
 *   either goes back again or re-arms.
 * - **A tab close or reload.** `beforeunload`, where the browser insists on
 *   its own wording.
 *
 * A programmatic `navigate()` inside the page is NOT caught. Pages that
 * navigate themselves already know they are doing it and can ask first.
 */
export function useUnsavedGuard(dirty: boolean, copy: UnsavedGuardCopy): void {
  // Read through refs so the listeners can stay mounted for the page's life
  // rather than being torn down and rebuilt on every keystroke.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const copyRef = useRef(copy);
  copyRef.current = copy;
  const askingRef = useRef(false);

  function ask(onDiscard: () => void, onKeep?: () => void) {
    if (askingRef.current) return;
    askingRef.current = true;
    openConfirmModal({
      title: copyRef.current.title,
      children: copyRef.current.body,
      labels: { confirm: copyRef.current.discard, cancel: copyRef.current.keep },
      confirmProps: { color: 'red' },
      onConfirm: () => { askingRef.current = false; onDiscard(); },
      onCancel: () => { askingRef.current = false; onKeep?.(); }
    });
  }

  // ── Link clicks ─────────────────────────────────────────────────────────
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!dirtyRef.current) return;
      // Let the browser have modified clicks: they open a new tab, so this
      // page and its work stay exactly where they are.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href') ?? '';
      if (href.startsWith('#') || /^[a-z]+:/i.test(href)) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;

      e.preventDefault();
      e.stopPropagation();
      ask(() => {
        dirtyRef.current = false;
        anchor.click();
      });
    }

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // ── Back and Forward ────────────────────────────────────────────────────
  useEffect(() => {
    if (!dirty) return;

    // The sentinel: one extra entry so the first Back lands here instead of
    // leaving the page.
    history.pushState({ ...history.state, rrUnsavedGuard: true }, '');

    function onPopState() {
      if (!dirtyRef.current) return;
      // Put the sentinel back immediately, so the page stays put while the
      // question is on screen.
      history.pushState({ ...history.state, rrUnsavedGuard: true }, '');
      ask(() => {
        dirtyRef.current = false;
        // Two entries back: the sentinel just re-pushed, and the one the user
        // was trying to leave from.
        history.go(-2);
      });
    }

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      // Clean the sentinel up when the work is saved, so Back does not need
      // two presses on a page with nothing to lose.
      if (history.state?.rrUnsavedGuard) history.back();
    };
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tab close and reload ────────────────────────────────────────────────
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // The browser shows its own message; assigning returnValue is what asks
      // it to prompt at all.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}
